import { FC, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ImagesApp } from '../../constants/images';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import { useResaleContext } from '../../hooks/contexts/resale.context';
import { useResaleEventContext } from '../../hooks/contexts/resaleEvent.context';
import { useUserContext } from '../../hooks/contexts/user.context';
import { usePriceDisplay } from '../../hooks/usePriceDisplay';
import { useResaleTracking } from '../../hooks/useResaleTracking';
import formatNumericValue from '../../utils/formatNumericValue';
import { logger } from '../../utils/logger';
import Auth from '../Auth/Auth';
import { BigButton } from '../Buttons';
import * as Styles from './styles';

type ResaleSummaryMobileProps = {
  /**
   * Optional price override (e.g. promotional/discounted value).
   * When provided and strictly lower than `resaleData.saleValue`,
   * it is used as the displayed price and the original `saleValue`
   * is rendered alongside with a strikethrough. Otherwise ignored.
   */
  overridePrice?: number;
  /**
   * When true, a FastPass offer is being resolved.
   * Renders skeleton instead of price and disables the proceed CTA.
   */
  offerPending?: boolean;
  /** Caminho 2 (waitlist): acquisition channel, forwarded to payment for conversion tracking. */
  source?: string;
  /** Caminho 2 (waitlist): WaitlistNotification UUID, forwarded to payment for conversion tracking. */
  waitlistId?: string;
};

const ResaleSummaryMobile: FC<ResaleSummaryMobileProps> = ({
  overridePrice,
  offerPending = false,
  source,
  waitlistId,
}) => {
  const [openDetails, setOpenDetails] = useState(false);
  const { translate } = useI18nContext();
  const { resaleData, loading } = useResaleContext();
  const { event } = useResaleEventContext();
  const { user, authenticatedUser } = useUserContext();
  const { setContentModal, toggleModal, defineTitleModal } = useModalContext();
  const { resaleTracking } = useResaleTracking();
  const navigate = useNavigate();
  const waitingForLogin = useRef(false);

  if (!resaleData) {
    return null;
  }

  const handleDetails = () => {
    setOpenDetails(!openDetails);
  };

  const handleProceedToPayment = () => {
    if (!authenticatedUser) {
      // Track login required
      if (resaleData && event) {
        resaleTracking.trackLoginRequired(resaleData, event, 'resale-summary-mobile-proceed-button');
      }

      waitingForLogin.current = true;
      defineTitleModal(translate(['INFORMATION', 'IDENTIFY_YOURSELF']));
      setContentModal(<Auth path="login" />);
      toggleModal({ open : true });
      return;
    }

    if (!user || !resaleData) {
      logger.warn('Cannot proceed to payment: missing user or resale data');
      return;
    }

    // Block seller from buying their own resale
    if (Number(user.id) === Number(resaleData.userId)) {
      navigate(`/resale/${resaleData.uuid}`, { state : { errorType : 'ownResale' } });
      return;
    }

    // Track proceed to payment clicked
    if (resaleData && event) {
      resaleTracking.trackProceedToPaymentClicked(
        resaleData,
        event,
        user,
        authenticatedUser || false,
        'resale-summary-mobile-proceed-button'
      );
    }

    // Navigate to resale payment page — forward overridePrice + Caminho 2 state
    const paymentState = {
      ...(source ? { source } : {}),
      ...(waitlistId ? { waitlistId } : {}),
      ...(overridePrice !== undefined ? { overridePrice } : {}),
    };
    navigate(`/resale/${resaleData.uuid}/payment`, {
      state : Object.keys(paymentState).length ? paymentState : undefined,
    });
  };

  useEffect(() => {
    if (waitingForLogin.current && authenticatedUser && user) {
      // Track login completed
      if (resaleData && event) {
        resaleTracking.trackLoginCompleted(resaleData, event, user);
      }

      waitingForLogin.current = false;
      toggleModal({ open : false });
    }
  }, [authenticatedUser, user, toggleModal, resaleData, event, resaleTracking]);

  const { displayPrice, hasDiscount } = usePriceDisplay(resaleData.saleValue, overridePrice);

  const formattedDisplayPrice = formatNumericValue({
    value : displayPrice,
    discount : 0,
    currencyFormat : 'BRL'
  });

  const formattedOriginalPrice = formatNumericValue({
    value : resaleData.saleValue,
    discount : 0,
    currencyFormat : 'BRL'
  });

  return (
    <Styles.Container
      $isOpen={openDetails}
      id="resale-summary-mb"
    >
      <Styles.RowFullWidth $isCopyright={false}>
        <Styles.Column>
          <Styles.TextInfo $weight={500} size={12}>
            1 {translate(['STEPS', 'TXT4'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={16}>
            {offerPending ? (
              <Styles.PriceSkeleton aria-label={translate(['FASTPASS', 'CALCULATING_PRICE'])} />
            ) : (
              <>
                {formattedDisplayPrice}
                {hasDiscount && (
                  <Styles.StrikethroughPrice>{formattedOriginalPrice}</Styles.StrikethroughPrice>
                )}
              </>
            )}
          </Styles.TextInfo>
        </Styles.Column>
        <Styles.Column>
          <button onClick={handleDetails}>
            <img
              src={openDetails ? ImagesApp.svg.arrowDown : ImagesApp.svg.arrowUp}
              alt="toggle details"
            />
          </button>
        </Styles.Column>
      </Styles.RowFullWidth>
      {openDetails && (
        <Styles.Detailing $isVisible={openDetails} id="resale-summary-detailing">
          <Styles.TicketRow>
            <Styles.TicketInfo>
              <Styles.TicketName>{resaleData.ticket.guestType.name}</Styles.TicketName>
              <Styles.TicketCategory>{resaleData.ticket.guestType.type}</Styles.TicketCategory>
            </Styles.TicketInfo>
            <Styles.TicketPrice>
              {offerPending ? (
                <Styles.PriceSkeleton aria-label={translate(['FASTPASS', 'CALCULATING_PRICE'])} />
              ) : (
                <>
                  {formattedDisplayPrice}
                  {hasDiscount && (
                    <Styles.StrikethroughPrice>{formattedOriginalPrice}</Styles.StrikethroughPrice>
                  )}
                </>
              )}
            </Styles.TicketPrice>
          </Styles.TicketRow>
        </Styles.Detailing>
      )}
      <Styles.ButtonWrapper>
        <BigButton
          disabled={loading || offerPending}
          onClick={handleProceedToPayment}
          id="resale-summary-mobile-proceed-button"
        >
          {translate(['STEPS', 'TXT15'])}
        </BigButton>
      </Styles.ButtonWrapper>
    </Styles.Container>
  );
};

export default ResaleSummaryMobile;
